<template>
  <div
    v-for="input in inputs as Record<string, any>[]"
    :key="input.formInputId?.value + input.label?.value"
    v-bind="adobeTargetDataTypes(input)"
    class="empty:hidden"
  >
    <slot :name="input.formInputId?.value ?? input.name">
      <template v-if="input.inputType?.value == FormInputTypes.ADDRESS_AUTOCOMPLETE">
        [Please use AddressLookupOptions component]
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.DOB">
        <DateOfBirth
          :bindings="formInputData(input.formInputId?.value)"
          :isPolicyholder="input?.isPolicyholder?.value"
          :isDriver="input?.isDriver?.value"
          :invalidDobWarning="input?.invalidDobWarning?.value"
          :maxDateByDays="input?.maxDateByDays?.value"
          :minDate="input?.minDate?.value"
          :dateMustBefore="input?.dateMustBefore?.value"
          :dateMustAfter="input?.dateMustAfter?.value"
        />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.DATE_INPUT">
        <DateInput
          :bindings="formInputData(input.formInputId?.value)"
          :minDateToday="input?.minDateToday?.value"
          :maxDateByDays="input?.maxDateByDays?.value"
          :minDate="input?.minDate?.value"
          :dateMustBefore="input?.dateMustBefore?.value"
          :dateMustAfter="input?.dateMustAfter?.value"
        />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.TITLE_NAME">
        <TitleNameInput
          :bindings="formInputData(input.formInputId?.value)"
          :firstnameMinLength="input?.firstnameMinLength.value"
          :firstnameMaxLength="input?.firstnameMaxLength.value"
          :lastnameMinLength="input?.lastnameMinLength.value"
          :lastnameMaxLength="input?.lastnameMaxLength.value"
          :titleRequiredMessage="input?.titleRequiredMessage?.value"
          :firstnameRequiredErrorMessage="input?.firstnameRequiredErrorMessage?.value"
          :firstnameRegex="input?.firstnameRegex?.value"
          :firstnameRegexErrorMessage="input?.firstnameRegexErrorMessage?.value"
          :lastnameRequiredErrorMessage="input?.lastnameRequiredErrorMessage?.value"
          :lastnameRegex="input?.lastnameRegex?.value"
          :lastnameRegexErrorMessage="input?.lastnameRegexErrorMessage?.value"
        />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.LABEL">
        <RichText :field="input.label" :class="input.outerClass?.value" />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.GENDER">
        <Gender :bindings="formInputData(input.formInputId?.value)" />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.CALL_TO_ACTION_CARD">
        <CallToActionCard :bindings="formInputData(input.formInputId?.value)" />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.DISCLAIMER">
        <Disclaimer :bindings="input" />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.SECTION_PANEL">
        <SectionPanel v-bind="input" />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.RADIO_CARD_LIST">
        <RadioCardList :bindings="formInputData(input.formInputId?.value)" />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.CUSTOM"> </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.PHONE_NUMBER">
        <PhoneNumberInput :bindings="formInputData(input.formInputId?.value)" />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.CTABANNER">
        <CTABanner
          :fields="{
            title: input?.title,
            subTitle: input?.subTitle,
            icon: input?.icon,
          }"
        />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.DATEPICKER">
        <DatePicker
          :bindings="formInputData(input.formInputId?.value)"
          :minDateToday="input?.minDateToday?.value"
          :maxDateByDays="input?.maxDateByDays?.value"
          :minDateValidationMessage="input?.minDateValidationMessage?.value"
          :maxDateValidationMessage="input?.maxDateValidationMessage?.value"
        />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.LOGINBUTTON">
        <AccountLoginInfoBox :fields="input" />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.PANEL_TITLE">
        <PanelTitle
          :bindings="formInputData(input.formInputId?.value)"
          :title="input.title!"
          :subTitle="input.subTitle!"
          :icon="input.icon"
          :isTextBlock="input.isTextBlock"
          :panelType="input.panelType"
        />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.CUSTOM_EMAIL_CONFIRMATION">
        [Please use SendEmail component]
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.FIND_CAR">
        <FormKit v-bind="input" type="findCar" />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.SLIDER">
        <RangeInput v-bind="input" />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.DIVIDER">
        <Divider :outerClass="input.outerClass?.value" />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.LINKPANEL">
        <LinkPanel
          :bindings="formInputData(input.formInputId?.value)"
          :heading="input.heading"
          :headingDescription="input.headingDescription"
          :subheading="input.subheading"
          :subheadingDescription="input.subheadingDescription"
          :links="input.fields"
          :isGreyBackground="input.isGreyBackground"
          :isArrowLinks="input.isArrowLinks"
          :iconSize="input.iconSize"
          :globalIcon="input.globalIcon"
          :globalButtonLabel="input.globalButtonLabel"
        />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.PROMO_CARD_MINI_LIST">
        <PromoCardMiniList
          :bindings="formInputData(input.formInputId?.value)"
          :promoCards="input.fields"
          :heading="input.heading"
          :cardLayout="input.cardLayout"
          :maxItemsToShow="input.maxItemsToShow"
        />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.PROMO_CARD_BASIC_LIST">
        <PromoCardBasicList
          :bindings="formInputData(input.formInputId?.value)"
          :promoCards="input.fields"
          :heading="input.heading"
          :cardLayout="input.cardLayout"
          :maxItemsToShow="input.maxItemsToShow"
        />
      </template>
      <template v-else-if="input.inputType?.value == FormInputTypes.TEXTAREA">
        <TextArea
          :bindings="formInputData(input.formInputId?.value)"
          :maxContentLength="formInputData(input.formInputId?.value).maxlength"
        />
      </template>
      <template v-else>
        <RACQFormKit v-if="input.inputType?.value" :bindings="getRacqFormKitBindings(input)" />
        <div v-else></div>
      </template>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { IFormStep, ISitecoreFormGenericInput } from '@/interfaces';
import { computed } from 'vue';
import { FormInputTypes } from '@/enums/form-input-types.enum';
import useFormInputData, { getAdobeTargetDataAttributes } from '@/composables/useFormInputData';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import DateInput from '@/components/DateInput/DateInput.vue';
import TitleNameInput from '@/components/TitleNameInput/TitleNameInput.vue';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import Gender from '@/components/Gender/Gender.vue';
import Disclaimer from '@/components/Disclaimer/Disclaimer.vue';
import CallToActionCard from '@/components/CallToActionCard/CallToActionCard.vue';
import SectionPanel from '@/components/SectionPanel/SectionPanel.vue';
import DatePicker from '@/components/DatePicker/DatePicker.vue';
import AccountLoginInfoBox from '@/components/AccountLoginInfoBox/AccountLoginInfoBox.vue';
import PhoneNumberInput from '@/components/PhoneNumberInput/PhoneNumberInput.vue';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import RadioCardList from '@/components/RadioCardList/RadioCardList.vue';
import RangeInput from '@/components/RangeInput/RangeInput.vue';
import DateOfBirth from '@/components/DateInput/DateOfBirth.vue';
import Divider from '@/components/Divider/Divider.vue';
import LinkPanel from '@/components/LinkPanel/LinkPanel.vue';
import PromoCardMiniList from '@/components/PromoCardMini/PromoCardMiniList.vue';
import PromoCardBasicList from '@/components/PromoCardBasic/PromoCardBasicList.vue';
import { FormKit } from '@formkit/vue';
import TextArea from '../TextArea/TextArea.vue';
import CTABanner from '../CTABanner/CTABanner.vue';

// Define Vue props
// ------------------------------------------------------------- //
const props = defineProps<{
  step: IFormStep;
}>();

const { formInputData, convertSitecoreToVueCheckboxListOptions } = useFormInputData(props.step.formInputs!);
const inputs = computed<ISitecoreFormGenericInput[]>(() =>
  props.step?.formInputs ? Object.values(props.step?.formInputs).filter((input) => !input?.isHidden) : []
);

const getRacqFormKitBindings = (input: any) => {
  if (input.inputType?.value == FormInputTypes.CHECKBOX && input.fields.length > 0) {
    return {
      ...formInputData(input.formInputId?.value),
      options: convertSitecoreToVueCheckboxListOptions(input),
    };
  } else {
    return formInputData(input.formInputId?.value);
  }
};

function adobeTargetDataTypes(input: Record<string, any>) {
  return getAdobeTargetDataAttributes(input, props.step.stepName!);
}
</script>
